import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Box, Button, Typography, TextField, InputAdornment, Card,
  Dialog, DialogActions, DialogContent, DialogTitle, Chip,
} from "@mui/material";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router-dom";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import Pagination from "../../../Components/Pagination";
import axios from "axios";

interface Deposit {
  id: number;
  date: string;
  amount: string | number;
  remarks: string;
  is_approved: boolean | number;
}

const DepositList = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const API_URL = process.env.REACT_APP_API_URL;

  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/view-deposit`)
      .then(r => setDeposits(r.data || []))
      .catch(() => showErrorMessage("Failed to load deposits."));
  }, [API_URL]);

  const filtered = deposits.filter(d =>
    (d.remarks || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(d.amount).includes(searchQuery) ||
    (d.date || "").includes(searchQuery)
  );

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDelete = (id: number) => {
    axios
      .delete(`${API_URL}/deposit/${id}`)
      .then(() => {
        setDeposits(prev => prev.filter(d => d.id !== id));
        showMessage("Deposit deleted successfully.");
      })
      .catch(() => showErrorMessage("Failed to delete deposit."));
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent="space-between"
        sx={{ borderBottom: 2, padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={4} md={4} sm={4} xs={4} pt={2} alignContent="center">
          <Typography className={classes.cardTitle}>Deposits</Typography>
        </Grid>
        <Grid item lg={8} md={8} sm={8} xs={8} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Grid container spacing={1} justifyContent="flex-end">
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: { root: classes.formControlList },
                }}
                fullWidth
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search deposits…"
                size="small"
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                className={classes.addButton}
                onClick={() => navigate("/deposit/create")}
              >
                Add Deposit
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ padding: 3 }}>
          <TableContainer>
            <Table className={classes.table}>
              <TableHead className={classes.tableHeaderRow}>
                <TableRow className={classes.tableRowHover}>
                  <TableCell className={classes.tablheader}><strong>Date</strong></TableCell>
                  <TableCell className={classes.tablheader}><strong>Amount</strong></TableCell>
                  <TableCell className={classes.tablheader}><strong>Remarks</strong></TableCell>
                  <TableCell className={classes.tablheader}><strong>Status</strong></TableCell>
                  <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length > 0 ? (
                  paginated.map(deposit => (
                    <TableRow key={deposit.id}>
                      <TableCell className={classes.tablCell}>{deposit.date}</TableCell>
                      <TableCell className={classes.tablCell}>${Number(deposit.amount).toFixed(2)}</TableCell>
                      <TableCell className={classes.tablCell}>{deposit.remarks || "—"}</TableCell>
                      <TableCell className={classes.tablCell}>
                        <Chip
                          label={deposit.is_approved ? "Approved" : "Pending"}
                          size="small"
                          sx={{
                            bgcolor: deposit.is_approved ? "#dcfce7" : "#fef9c3",
                            color:   deposit.is_approved ? "#166534" : "#854d0e",
                            fontWeight: 600, fontSize: 11,
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Grid container spacing={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Grid item>
                            <Button
                              variant="contained"
                              className={classes.editButtonList}
                              onClick={() => navigate(`/deposit/edit/${deposit.id}`)}
                            >
                              <EditSharpIcon className={classes.editListIcon} />
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              variant="contained"
                              className={classes.deleteButtonList}
                              onClick={() => { setDeleteTargetId(deposit.id); setConfirmDeleteOpen(true); }}
                            >
                              <DeleteOutlineSharpIcon className={classes.deleteListIcon} />
                            </Button>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No deposits found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Grid spacing={5} p="18px" py={4} justifyContent="end" container textAlign="center" alignContent="center" alignItems="center">
              <Grid sx={{ textAlign: "end", justifyContent: "flex-end", alignItems: "flex-end" }}>
                <Pagination
                  count={Math.ceil(filtered.length / rowsPerPage)}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(_, newPage) => setPage(newPage)}
                />
              </Grid>
            </Grid>
          </TableContainer>
        </Card>
      </Grid>

      {/* Delete Confirmation */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Deposit</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>Are you sure you want to delete this deposit? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} variant="outlined">Cancel</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}
            onClick={() => {
              if (deleteTargetId !== null) handleDelete(deleteTargetId);
              setConfirmDeleteOpen(false);
              setDeleteTargetId(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DepositList;
